import { Component, Input } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  CheckoutExtensionUI,
  CheckoutProductUpgradeExtension,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { type IDynamicFieldsOptions } from '@pdqdev/pdq-shared/dist/interfaces/IDynamicFields';
import { capitalize } from '@utils/string-utils';
import { BehaviorSubject } from 'rxjs';
import {
  AppearanceFormGroup,
  BadgeFormGroup,
  CompletionBannerFormGroup,
  CompletionBannerTone,
  TextAppearanceFormGroup,
  UpgradeFormGroup,
} from '../../models';

@Component({
  selector: 'product-upgrade-appearance',
  templateUrl: './product-upgrade-appearance.component.html',
  styleUrls: ['./product-upgrade-appearance.component.scss'],
  standalone: false,
})
export class ProductUpgradeAppearanceComponent {
  @Input() formGroup!: FormGroup<UpgradeFormGroup>;
  @Input() dynamicFieldsAutocompleteOptions$!: BehaviorSubject<IDynamicFieldsOptions[]>;

  readonly richTextConfig = {
    triggerChar: '{',
    filterByKey: 'searchTerm',
    removeTriggerCharOnSelect: true,
    descriptionKey: 'description',
    variant: 'categorizedOptions',
  };

  readonly buttonDisabledFormats = ['italic', 'bold', 'underline', 'strike', 'align', 'list', 'header'];

  readonly appearanceOptions = Object.values(CheckoutExtensionUI.Appearance).map((value) => ({
    label: capitalize(value),
    value,
  }));

  readonly borderOptions = Object.values(CheckoutExtensionUI.BorderType).map((value) => ({
    label: capitalize(value),
    value,
  }));

  readonly borderWidthOptions = Object.values(CheckoutExtensionUI.BorderWidth).map((value) => ({
    label: capitalize(value),
    value,
  }));

  readonly cornerRadiusOptions = Object.values(CheckoutExtensionUI.CornerRadius).map((value) => ({
    label: capitalize(value),
    value,
  }));

  readonly mainBackgroundCornerRadiusOptions = Object.values(CheckoutExtensionUI.CornerRadius)
    .filter((value) => value !== CheckoutExtensionUI.CornerRadius.FULLY_ROUNDED)
    .map((value) => ({ label: capitalize(value), value }));

  readonly paddingOptions = Object.values(CheckoutExtensionUI.Padding).map((value) => ({
    label: capitalize(value),
    value,
  }));

  readonly bannerToneOptions: { label: string; value: CompletionBannerTone; colorTag: string }[] = [
    { label: 'Success', value: 'success', colorTag: 'var(--success-default)' },
    { label: 'Info', value: 'info', colorTag: 'var(--on-surface-default)' },
    { label: 'Warning', value: 'warning', colorTag: 'var(--warning-default)' },
    { label: 'Critical', value: 'critical', colorTag: 'var(--error-default)' },
  ];

  get appearance(): FormGroup<AppearanceFormGroup> {
    return this.formGroup.get('appearance') as FormGroup<AppearanceFormGroup>;
  }

  get titleText(): FormGroup<TextAppearanceFormGroup> {
    return this.appearance.get('titleText') as FormGroup<TextAppearanceFormGroup>;
  }

  get upgradeText(): FormGroup<TextAppearanceFormGroup> {
    return this.appearance.get('upgradeText') as FormGroup<TextAppearanceFormGroup>;
  }

  get titleBadge(): FormGroup<BadgeFormGroup> {
    return this.titleText.get('badge') as FormGroup<BadgeFormGroup>;
  }

  get upgradeTextBadge(): FormGroup<BadgeFormGroup> {
    return this.upgradeText.get('badge') as FormGroup<BadgeFormGroup>;
  }

  get titleLabelControl(): FormControl<string> {
    return this.titleText.get('label') as FormControl<string>;
  }

  get upgradeLabelControl(): FormControl<string> {
    return this.upgradeText.get('label') as FormControl<string>;
  }

  get buttonLabelControl(): FormControl<string> {
    return this.appearance.get('button.label') as FormControl<string>;
  }

  get buttonVariantsLabelControl(): FormControl<string> {
    return this.appearance.get('button.labelForProductsWithVariants') as FormControl<string>;
  }

  get completionBanner(): FormGroup<CompletionBannerFormGroup> {
    return this.appearance.get('completionBanner') as FormGroup<CompletionBannerFormGroup>;
  }

  get completionBannerTextControl(): FormControl<string> {
    return this.completionBanner.get('text') as FormControl<string>;
  }

  get completionBannerEnabledControl(): FormControl<boolean> {
    return this.completionBanner.get('enabled') as FormControl<boolean>;
  }

  isContainerCollapsed = false;
  isImageCollapsed = false;
  isTitleCollapsed = false;
  isUpgradeTextCollapsed = false;
  isButtonsCollapsed = false;
  isCompletionBannerCollapsed = true;

  get isFocusedStandalone(): boolean {
    const settings = this.formGroup.get('settings');
    return (
      settings?.get('layoutStyle')?.value === 'focused' &&
      settings?.get('displayMode')?.value === CheckoutProductUpgradeExtension.DisplayMode.STANDALONE_BLOCK
    );
  }
}
